import { zodTransformFirestoreTimestampToDate } from "../../utils/zodUtils.mjs";
import { zodFlavorId } from "../ids.mjs";
import { z as z$1 } from "zod";

// src/models/consultation/clinicalReservations.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
/** Sibling of clinical availability slots — clinical reservation namespace only. */
const CLINICAL_RESERVATIONS_DOC_ID = "clinical_reservations";
const CLINICAL_RESERVATION_WINDOW_MS = 9e5;
/** Max checkout hold duration from first reserve (sliding window capped here). */
const CLINICAL_HOLD_MAX_DURATION_MS = 36e5;
function remainingClinicalHoldMinutes(checkoutExpiresAt, now = /* @__PURE__ */ new Date()) {
	if (!checkoutExpiresAt) return null;
	const remainingMs = checkoutExpiresAt.getTime() - now.getTime();
	if (remainingMs <= 0) return 0;
	return Math.ceil(remainingMs / 6e4);
}
function computeClinicalHoldExtensionExpiry(args) {
	const capExpiresAt = new Date(args.reservedAt.getTime() + CLINICAL_HOLD_MAX_DURATION_MS);
	const slidingExpiresAt = new Date(args.now.getTime() + CLINICAL_RESERVATION_WINDOW_MS);
	const expiresAt = slidingExpiresAt.getTime() <= capExpiresAt.getTime() ? slidingExpiresAt : capExpiresAt;
	return {
		expiresAt,
		holdCapReached: expiresAt.getTime() === capExpiresAt.getTime()
	};
}
/**
* Composite slot key (Q43):
* `{consultType}:{tellescopeUserId}:{startTimeInMS}:{durationInMinutes}`
*/
function buildClinicalReservedSlotKey(args) {
	return [
		args.consultType,
		args.tellescopeUserId,
		String(args.startTimeInMS),
		String(args.durationInMinutes)
	].join(":");
}
const clinicalReservationEntrySchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId(),
	tellescopeUserId: z$1.string().min(1),
	startTimeInMS: z$1.number().int().nonnegative(),
	durationInMinutes: z$1.number().int().positive(),
	expiresAt: firestoreDate
});
const clinicalReservationsDocSchema = z$1.object({
	reservations: z$1.record(z$1.string(), clinicalReservationEntrySchema),
	updatedAt: firestoreDate
});
function reservationInterval(args) {
	return {
		start: args.startTimeInMS,
		end: args.startTimeInMS + args.durationInMinutes * 6e4
	};
}
/** True when two half-open intervals [start, end) overlap. */
function intervalsOverlap(a, b) {
	return a.start < b.end && b.start < a.end;
}
/**
* Returns true if an active (non-expired) reservation for the same NP overlaps
* the candidate slot interval. Optionally ignore a consultation's own hold.
*/
function hasOverlappingClinicalReservation(args) {
	const now = args.now ?? /* @__PURE__ */ new Date();
	const candidate = reservationInterval(args);
	for (const entry of Object.values(args.reservations)) {
		if (entry.tellescopeUserId !== args.tellescopeUserId) continue;
		if (args.ignoreConsultationId && entry.consultationId === args.ignoreConsultationId) continue;
		if (entry.expiresAt.getTime() <= now.getTime()) continue;
		if (intervalsOverlap(candidate, reservationInterval(entry))) return true;
	}
	return false;
}

export { CLINICAL_HOLD_MAX_DURATION_MS, CLINICAL_RESERVATIONS_DOC_ID, CLINICAL_RESERVATION_WINDOW_MS, buildClinicalReservedSlotKey, clinicalReservationEntrySchema, clinicalReservationsDocSchema, computeClinicalHoldExtensionExpiry, hasOverlappingClinicalReservation, intervalsOverlap, remainingClinicalHoldMinutes, reservationInterval };
//# sourceMappingURL=clinicalReservations.mjs.map